import React from "react";
import {
  FaTimes,
  FaPhone,
  FaUser,
  FaCalendar,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaUserTie,
  FaCommentAlt,
} from "react-icons/fa";

const IssueDetailModal = ({ show, issue, onClose }) => {
  if (!show || !issue) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case "Pending":
        return <FaExclamationTriangle className="text-yellow-500" />;
      case "In progress":
        return <FaClock className="text-blue-500" />;
      case "Resolved":
        return <FaCheckCircle className="text-green-500" />;
      default:
        return <FaExclamationTriangle className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "In progress":
        return "text-blue-600 bg-blue-100 border-blue-200";
      case "Resolved":
        return "text-green-600 bg-green-100 border-green-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border max-w-2xl shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Issue Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            <FaTimes />
          </button>
        </div>

        {/* Issue Information */}
        <div className="space-y-6">
          {/* Status and Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <div
                className={`flex items-center px-3 py-2 border rounded-lg ${getStatusColor(
                  issue.status
                )}`}
              >
                {getStatusIcon(issue.status)}
                <span className="ml-2 font-medium">{issue.status}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issue Type
              </label>
              <div className="flex items-center px-3 py-2 border rounded-lg bg-gray-50">
                <FaExclamationTriangle className="text-orange-500 mr-2" />
                <span>{issue.issueType}</span>
              </div>
            </div>
          </div>

          {/* Title and Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issue Title
            </label>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {issue.title}
            </h3>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <div className="p-4 border rounded-lg bg-gray-50">
              <p className="text-gray-700">{issue.message}</p>
            </div>
          </div>

          {/* Additional Message */}
          {issue.additionalMessage && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Information
              </label>
              <div className="p-4 border rounded-lg bg-blue-50">
                <div className="flex items-start">
                  <FaCommentAlt className="text-blue-500 mt-1 mr-2" />
                  <p className="text-gray-700">{issue.additionalMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Customer Information */}
          <div className="border-t pt-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">
              Customer Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <FaPhone className="text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="font-medium">{issue.phone}</p>
                </div>
              </div>
              <div className="flex items-center">
                <FaUser className="text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Role</p>
                  <p className="font-medium capitalize">{issue.role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Assignment Information */}
          <div className="border-t pt-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">
              Assignment Details
            </h4>
            {issue.assignedTo ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <FaUserTie className="text-green-600 mr-2" />
                  <span className="font-medium text-green-800">
                    Assigned to {issue.assignedTo.fullName}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">
                      Role:{" "}
                      <span className="font-medium capitalize">
                        {issue.assignedTo.role}
                      </span>
                    </p>
                    <p className="text-gray-600">
                      Phone:{" "}
                      <span className="font-medium">
                        {issue.assignedTo.phone}
                      </span>
                    </p>
                  </div>
                  {issue.assignedBy && (
                    <div>
                      <p className="text-gray-600">
                        Assigned by:{" "}
                        <span className="font-medium">
                          {issue.assignedBy.fullName}
                        </span>
                      </p>
                      <p className="text-gray-600">
                        Assigned on:{" "}
                        <span className="font-medium">
                          {new Date(issue.assignedAt).toLocaleDateString()} at{" "}
                          {new Date(issue.assignedAt).toLocaleTimeString()}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <FaExclamationTriangle className="text-yellow-600 mr-2" />
                  <span className="font-medium text-yellow-800">
                    This issue is not assigned to anyone yet
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Resolution Information */}
          {issue.status === "Resolved" && (
            <div className="border-t pt-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Resolution Details
              </h4>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                {issue.resolutionNotes && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Resolution Notes
                    </p>
                    <p className="text-gray-700">{issue.resolutionNotes}</p>
                  </div>
                )}
                {issue.contactNotes && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Contact Notes
                    </p>
                    <p className="text-gray-700">{issue.contactNotes}</p>
                  </div>
                )}
                <div className="text-sm text-gray-600">
                  <p>
                    Resolved by:{" "}
                    <span className="font-medium">
                      {issue.resolvedBy?.fullName || "System"}
                    </span>
                  </p>
                  <p>
                    Resolved on:{" "}
                    <span className="font-medium">
                      {issue.resolvedAt
                        ? new Date(issue.resolvedAt).toLocaleDateString()
                        : "N/A"}{" "}
                      at{" "}
                      {issue.resolvedAt
                        ? new Date(issue.resolvedAt).toLocaleTimeString()
                        : "N/A"}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="border-t pt-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">Timeline</h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <FaCalendar className="text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium">
                    {new Date(issue.createdAt).toLocaleDateString()} at{" "}
                    {new Date(issue.createdAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>

              {issue.assignedAt && (
                <div className="flex items-center">
                  <FaUserTie className="text-blue-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Assigned</p>
                    <p className="font-medium">
                      {new Date(issue.assignedAt).toLocaleDateString()} at{" "}
                      {new Date(issue.assignedAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              )}

              {issue.contactedAt && (
                <div className="flex items-center">
                  <FaPhone className="text-green-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Customer Contacted</p>
                    <p className="font-medium">
                      {new Date(issue.contactedAt).toLocaleDateString()} at{" "}
                      {new Date(issue.contactedAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              )}

              {issue.resolvedAt && (
                <div className="flex items-center">
                  <FaCheckCircle className="text-green-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Resolved</p>
                    <p className="font-medium">
                      {new Date(issue.resolvedAt).toLocaleDateString()} at{" "}
                      {new Date(issue.resolvedAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default IssueDetailModal;
